import SearchPanel from '@Components/Search/SearchPanel'
import TabsWrapperComponent, { Tab } from '@Components/TabsWrapperComponent'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { ResultPage } from '@Entities/result-page'
import { OverclaimDocumentSearchResult } from '@Entities/overclaim/overclaim-document-search-result'
import React from 'react'
import OverclaimDocumentSelectionTable from '../OverclaimDocumentSelectionTable'
import { DEFAULT_PAGINATION, OverclaimDocumentSearchQuery } from '../search-config'

interface ExistingDocumentsTabsProps {
    activeSearchQuery: Partial<OverclaimDocumentSearchQuery>
    hasRunSearch: boolean
    recentlyUsedDocumentsPage?: ResultPage<OverclaimDocumentSearchResult>
    recentlyUsedLoading: boolean
    recentlyUsedPagination: PaginationBody
    recentDocumentsPaginationInfo?: PaginationInfo
    rightsholderId: number
    currentOverclaimHeaderId?: number
    searchCriteriaStore: SearchCriteriaStore<OverclaimDocumentSearchQuery>
    searchedDocumentsPage?: ResultPage<OverclaimDocumentSearchResult>
    searchedDocumentsPaginationInfo?: PaginationInfo
    searchLoading: boolean
    searchPagination: PaginationBody
    selectedDocumentIds: Set<number>
    loadRecentlyUsedDocuments: (pagination: PaginationBody) => void
    loadSearchedDocuments: (searchQuery: Partial<OverclaimDocumentSearchQuery>, pagination: PaginationBody) => void
    onSelectDocument: (document: OverclaimDocumentSearchResult) => void
    onUnselectDocument: (document: OverclaimDocumentSearchResult) => void
    resetSearchResults: () => void
}

const ExistingDocumentsTabs = ({
    activeSearchQuery,
    hasRunSearch,
    recentlyUsedDocumentsPage,
    recentlyUsedLoading,
    recentlyUsedPagination,
    recentDocumentsPaginationInfo,
    rightsholderId,
    currentOverclaimHeaderId,
    searchCriteriaStore,
    searchedDocumentsPage,
    searchedDocumentsPaginationInfo,
    searchLoading,
    searchPagination,
    selectedDocumentIds,
    loadRecentlyUsedDocuments,
    loadSearchedDocuments,
    onSelectDocument,
    onUnselectDocument,
    resetSearchResults,
}: ExistingDocumentsTabsProps) => {
    const tabs: Tab[] = [
        {
            tabName: 'Recently Used Documents',
            tabDisplayName: 'Recently Used Documents',
            tabComponent: (
                <OverclaimDocumentSelectionTable
                    title="Recently Used Documents"
                    rightsholderId={rightsholderId}
                    currentOverclaimHeaderId={currentOverclaimHeaderId}
                    resultsPage={recentlyUsedDocumentsPage}
                    paginationInfo={recentDocumentsPaginationInfo}
                    limit={recentlyUsedPagination.limit ?? 10}
                    loading={recentlyUsedLoading}
                    selectedDocumentIds={selectedDocumentIds}
                    onPageChange={(pagination) =>
                        loadRecentlyUsedDocuments({ ...recentlyUsedPagination, ...pagination })
                    }
                    onSelectDocument={onSelectDocument}
                    onUnselectDocument={onUnselectDocument}
                />
            ),
        },
        {
            tabName: 'Search Existing Documents',
            tabDisplayName: 'Search Existing Documents',
            tabComponent: (
                <div className="search-tab-content">
                    <SearchPanel<OverclaimDocumentSearchQuery, Record<string, never>>
                        searchCriteriaStore={searchCriteriaStore}
                        onSearch={(searchQuery) => loadSearchedDocuments(searchQuery, DEFAULT_PAGINATION)}
                        disabled={searchLoading}
                        onReset={resetSearchResults}
                    />
                    <OverclaimDocumentSelectionTable
                        title="Matching Documents"
                        rightsholderId={rightsholderId}
                        currentOverclaimHeaderId={currentOverclaimHeaderId}
                        resultsPage={searchedDocumentsPage}
                        paginationInfo={searchedDocumentsPaginationInfo}
                        limit={searchPagination.limit ?? 10}
                        loading={searchLoading}
                        selectedDocumentIds={selectedDocumentIds}
                        showEmptyState={hasRunSearch}
                        onPageChange={(pagination) =>
                            loadSearchedDocuments(activeSearchQuery, { ...searchPagination, ...pagination })
                        }
                        onSelectDocument={onSelectDocument}
                        onUnselectDocument={onUnselectDocument}
                    />
                </div>
            ),
        },
    ]

    return <TabsWrapperComponent tabs={tabs} defaultTab={tabs[0]} preventScrollReset />
}

export default ExistingDocumentsTabs
